import axios from 'axios'
import {
  superAdminListInstructionContent,
  superAdminCreateInstructionContent,
  superAdminUpdateInstructionContent,
  superAdminAddInstructionContentVersion,
  superAdminDeleteInstructionContent,
  superAdminInstructionContentUploadUrl,
  superAdminInstructionContentUrl,
  superAdminListInstructions,
  superAdminCreateInstructionModule,
  superAdminUpdateInstructionModule,
  superAdminDeleteInstruction,
  InstructionContent,
  InstructionContentPayload,
  InstructionModulePayload,
  Instruction,
} from '@training24/course/src/instruction'

interface InstructionLibraryState {
  items: InstructionContent[]
  loaded: boolean
  templates: Instruction[]
  templatesLoaded: boolean
}

export type { InstructionContent, Instruction }

export default {
  namespaced: true,

  state: (): InstructionLibraryState => ({
    items: [],
    loaded: false,
    templates: [],
    templatesLoaded: false,
  }),

  getters: {
    isLoaded: (state: InstructionLibraryState): boolean => state.loaded,
    getItems: (state: InstructionLibraryState): InstructionContent[] => state.items,
    getTemplates: (state: InstructionLibraryState): Instruction[] => state.templates,
    // contentId → Titel, um Sektionen einer Vorlage beim Bearbeiten zu benennen.
    getContentTitle: (state: InstructionLibraryState) => (contentId: string): string => {
      const c = state.items.find((x) => x.contentId === contentId)
      return c ? c.title : ''
    },
  },

  actions: {
    async LOAD(context: any, opts?: { force?: boolean }) {
      if (context.state.loaded && !(opts && opts.force)) return
      const jwt = context.rootGetters['getJWT']
      const items = await superAdminListInstructionContent(jwt)
      context.commit('SET', items)
    },

    // Pre-signed Anzeige-URL zur aktuellen Version eines MEDA-Dokuments (Vorschau).
    async GET_DOC_URL(context: any, { contentId }: { contentId: string }) {
      const jwt = context.rootGetters['getJWT']
      return superAdminInstructionContentUrl(contentId, jwt)
    },

    async CREATE(context: any, payload: InstructionContentPayload) {
      const jwt = context.rootGetters['getJWT']
      const item = await superAdminCreateInstructionContent(payload, jwt)
      context.commit('UPSERT', item)
      return item
    },

    async UPDATE(
      context: any,
      { contentId, payload }: { contentId: string; payload: Partial<InstructionContentPayload> }
    ) {
      const jwt = context.rootGetters['getJWT']
      const item = await superAdminUpdateInstructionContent(contentId, payload, jwt)
      context.commit('UPSERT', item)
      return item
    },

    async ADD_VERSION(context: any, { contentId, payload }: { contentId: string; payload: any }) {
      const jwt = context.rootGetters['getJWT']
      const item = await superAdminAddInstructionContentVersion(contentId, payload, jwt)
      context.commit('UPSERT', item)
      return item
    },

    async DELETE(context: any, contentId: string) {
      const jwt = context.rootGetters['getJWT']
      await superAdminDeleteInstructionContent(contentId, jwt)
      context.commit('REMOVE', contentId)
    },

    // Datei in den MEDA-Bibliotheks-Bucket laden und die Versions-Felder zurückgeben.
    async UPLOAD_FILE(context: any, { file }: { file: File }) {
      const jwt = context.rootGetters['getJWT']
      const contentType = file.type
      const fileType: 'pdf' | 'docx' = contentType === 'application/pdf' ? 'pdf' : 'docx'
      const { uploadUrl, key } = await superAdminInstructionContentUploadUrl(file.name, contentType, jwt)
      await axios.put(uploadUrl, file, { headers: { 'Content-Type': contentType } })
      return { fileName: file.name, fileKey: key, fileType }
    },

    // Komfort: Baustein anlegen + Datei hochladen + erste Version in einem Schritt.
    async CREATE_WITH_FILE(
      context: any,
      { title, file, confirmationItems, published }:
        { title: string; file: File; confirmationItems?: string[]; published?: boolean }
    ): Promise<InstructionContent> {
      const fileType: 'pdf' | 'docx' = file.type === 'application/pdf' ? 'pdf' : 'docx'
      const content = await context.dispatch('CREATE', {
        title,
        fileType,
        confirmationItems: confirmationItems || [],
        published: !!published,
      })
      const versionPayload = await context.dispatch('UPLOAD_FILE', { file })
      return context.dispatch('ADD_VERSION', { contentId: content.contentId, payload: versionPayload })
    },

    async ADD_VERSION_FILE(context: any, { contentId, file }: { contentId: string; file: File }) {
      const versionPayload = await context.dispatch('UPLOAD_FILE', { file })
      return context.dispatch('ADD_VERSION', { contentId, payload: versionPayload })
    },

    // ── Vorlagen-Unterweisungen (MEDA-Module) ──
    async LOAD_TEMPLATES(context: any, opts?: { force?: boolean }) {
      if (context.state.templatesLoaded && !(opts && opts.force)) return
      const jwt = context.rootGetters['getJWT']
      const items = await superAdminListInstructions(jwt)
      context.commit('SET_TEMPLATES', items)
    },

    async CREATE_TEMPLATE(context: any, payload: InstructionModulePayload) {
      const jwt = context.rootGetters['getJWT']
      const item = await superAdminCreateInstructionModule(payload, jwt)
      context.commit('UPSERT_TEMPLATE', item)
      return item
    },

    async UPDATE_TEMPLATE(
      context: any,
      { instructionId, payload }: { instructionId: string; payload: Partial<InstructionModulePayload> }
    ) {
      const jwt = context.rootGetters['getJWT']
      const item = await superAdminUpdateInstructionModule(instructionId, payload, jwt)
      context.commit('UPSERT_TEMPLATE', item)
      return item
    },

    async DELETE_TEMPLATE(context: any, instructionId: string) {
      const jwt = context.rootGetters['getJWT']
      await superAdminDeleteInstruction(instructionId, jwt)
      context.commit('REMOVE_TEMPLATE', instructionId)
    },
  },

  mutations: {
    SET(state: InstructionLibraryState, items: InstructionContent[]) {
      state.items = items
      state.loaded = true
    },
    UPSERT(state: InstructionLibraryState, item: InstructionContent) {
      const others = state.items.filter((c) => c.contentId !== item.contentId)
      state.items = [...others, item]
    },
    REMOVE(state: InstructionLibraryState, contentId: string) {
      state.items = state.items.filter((c) => c.contentId !== contentId)
    },
    SET_TEMPLATES(state: InstructionLibraryState, items: Instruction[]) {
      state.templates = items
      state.templatesLoaded = true
    },
    UPSERT_TEMPLATE(state: InstructionLibraryState, item: Instruction) {
      const others = state.templates.filter((t) => t.instructionId !== item.instructionId)
      state.templates = [...others, item]
    },
    REMOVE_TEMPLATE(state: InstructionLibraryState, instructionId: string) {
      state.templates = state.templates.filter((t) => t.instructionId !== instructionId)
    },
  },
}
